import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Star, MapPin, Wrench } from "lucide-react";
import type { Tool, User } from "@/lib/types";
import { CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/types";

interface ToolCardProps {
	tool: Tool & { owner?: User };
	onClick: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
	"power-tools": "zap",
	"hand-tools": "hammer",
	garden: "flower2",
	automotive: "car",
	cleaning: "sparkles",
	painting: "paintbrush",
	plumbing: "droplets",
	electrical: "plug",
	moving: "truck",
	other: "package",
};

export function ToolCard({ tool, onClick }: ToolCardProps) {
	return (
		<Card
			className="group cursor-pointer hover:shadow-md transition-all duration-200 hover:border-primary/20 overflow-hidden"
			onClick={onClick}
		>
			{/* Tool image placeholder */}
			<div className="h-36 bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center relative overflow-hidden">
				<Wrench className="w-12 h-12 text-muted-foreground/30 group-hover:scale-110 transition-transform" />
				<div className="absolute top-2 left-2">
					<Badge variant="secondary" className="text-[10px] bg-background/80 backdrop-blur-sm">
						{CATEGORY_LABELS[tool.category]}
					</Badge>
				</div>
				<div className="absolute top-2 right-2">
					<Badge
						variant="outline"
						className="text-[10px] bg-background/80 backdrop-blur-sm"
					>
						{CONDITION_LABELS[tool.condition]}
					</Badge>
				</div>
			</div>

			<CardContent className="p-3">
				<h3 className="font-medium text-sm leading-tight line-clamp-1 group-hover:text-primary transition-colors">
					{tool.name}
				</h3>
				<p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
					{tool.description}
				</p>

				<div className="flex items-center justify-between mt-3">
					<div className="flex items-center gap-1">
						<Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
						<span className="text-xs font-medium">{tool.rating}</span>
						<span className="text-xs text-muted-foreground">
							({tool.reviewCount})
						</span>
					</div>
					<span className="font-semibold text-sm">
						${tool.dailyRate}
						<span className="text-xs font-normal text-muted-foreground">
							/day
						</span>
					</span>
				</div>

				<div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
					{tool.owner && (
						<div className="flex items-center gap-1.5">
							<Avatar className="h-5 w-5">
								<AvatarImage src={tool.owner.avatar} />
								<AvatarFallback className="text-[8px]">
									{tool.owner.name[0]}
								</AvatarFallback>
							</Avatar>
							<span className="text-xs text-muted-foreground">
								{tool.owner.name.split(" ")[0]}
							</span>
						</div>
					)}
					<span className="text-xs text-muted-foreground flex items-center gap-0.5">
						<MapPin className="w-3 h-3" />
						{tool.neighborhood}
					</span>
				</div>
			</CardContent>
		</Card>
	);
}
